/**
 * Page manager screen watchers
 * @module services/pm-screen/watchers
 */
import { Map } from 'immutable'
import { isSynced as resolverIsSynced } from '../resolver/synced'
import { SET_RESOLVER_DATA } from '../resolver/actions'
import { getUserLanguagePrimary } from '../user/index'
import { BOOTSTRAP_PHASE_COMPLETE } from '../app/index'
import { SET_APP_BOOTSTRAP_PHASE } from '../app/actions'
import { SET_USER_DATA_LANGUAGE } from '../user/actions'
import { SET_AUTH_LOGIN_SUCCESS } from '../auth/actions'
import { getMultiplePmSections as getMultiplePmSectionsAction } from '../pm-section/actions'
import { PM_SCREEN_TYPE_MEMBER_HOME, PM_SCREEN_TYPE_CENTERS } from './types'
import { MEMBER_HOME_SECTIONS_LIMIT } from './ssrPmScreen'
import { GET_PM_SCREEN, SET_PM_SCREEN, getPmScreen as getPmScreenAction } from './actions'
import { getPmScreen } from './watchers-helpers'
import { getPmScreenTypeFromResolver, getPmScreenSectionSectionIds } from '.'

/**
 * Load a page manager screen by path
 */
export function onPmScreenShouldLoad ({ after }) {
  return after(
    [
      SET_RESOLVER_DATA,
      SET_APP_BOOTSTRAP_PHASE,
      SET_USER_DATA_LANGUAGE,
      SET_AUTH_LOGIN_SUCCESS,
    ],
    async ({ dispatch, state }) => {
      const {
        app = Map(),
        auth = Map(),
        user = Map(),
        resolver = Map(),
      } = state
      const language = getUserLanguagePrimary(user)
      const resolverSynced = resolverIsSynced(resolver, global.location)
      const resolverProcessing = resolver.get('processing')
      const bootstrapComplete = app.get('bootstrapPhase') === BOOTSTRAP_PHASE_COMPLETE
      if (!bootstrapComplete || resolverProcessing || !resolverSynced) {
        return
      }
      const pmScreenType = getPmScreenTypeFromResolver({ resolver, auth })

      if (pmScreenType) {
        // Member home preloads MEMBER_HOME_SECTIONS_LIMIT rows; remainder via Load More.
        let sectionsLimit = MEMBER_HOME_SECTIONS_LIMIT
        switch (pmScreenType) {
          case PM_SCREEN_TYPE_CENTERS:
            sectionsLimit = 10
            break
          case PM_SCREEN_TYPE_MEMBER_HOME:
          default:
            sectionsLimit = MEMBER_HOME_SECTIONS_LIMIT
            break
        }

        dispatch(getPmScreenAction({ screenType: pmScreenType, language, sectionsLimit }))
      }
    },
  )
}

/**
 * Handle get page manager screen action
 */
export function onGetPmScreen ({ after }) {
  return after(GET_PM_SCREEN, async ({ dispatch, state, action }) => {
    const { payload: { screenType, language, sectionsLimit } = {} } = action
    const {
      auth = Map(),
      pmScreen = Map(),
      pmSection = Map(),
      resolver = Map(),
      spotlightHero = Map(),
    } = state

    await getPmScreen({
      dispatch,
      auth,
      pmScreen,
      pmSection,
      resolver,
      spotlightHero,
      screenType,
      language,
      sectionsLimit,
    })
  })
}

/**
 * Load remaining sections after pmScreen is set
 * For centers/topics, load all sections that weren't included in the initial query
 */
export function onPmScreenSetLoadRemainingSections ({ after }) {
  return after(SET_PM_SCREEN, async ({ dispatch, state, action }) => {
    const {
      payload: { screenType, language } = {},
    } = action
    const {
      pmScreen = Map(),
      pmSection = Map(),
    } = state

    // Only load remaining sections for centers (topics)
    if (screenType !== PM_SCREEN_TYPE_CENTERS) {
      return
    }

    // Get all section IDs from pmScreen
    const sectionIds = getPmScreenSectionSectionIds({ pmScreen, screenType, language })
    if (!sectionIds || sectionIds.size === 0) {
      return
    }

    // Find sections that don't have data yet
    const sectionsToLoad = sectionIds.filter((sectionId) => {
      const sectionData = pmSection.get(sectionId)
      return !sectionData || !sectionData.get('data')
    })

    if (sectionsToLoad.size > 0) {
      // Load remaining sections that weren't included in the initial query
      dispatch(getMultiplePmSectionsAction({
        sectionIds: sectionsToLoad.toArray(),
        screenType,
      }))
    }
  })
}
